import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getTimelineData,
  getUpcomingWorkflowStageRequest,
  resetTimelineData,
} from 'src/redux/Actions/VehicleTracking';
import { ReactComponent as EntryIcon } from 'src/Images/icons/common/entry-log.svg';
import { ReactComponent as ExitIcon } from 'src/Images/icons/common/exit-log.svg';
import { ReactComponent as ChangeStationIcon } from 'src/Images/icons/common/changeStationIcon.svg';
import { Drawer } from '@mui/material';
import UpcomingWorkflowStageRequestDrawer from 'src/components/VehicleTrackingDetails/VehicleTrackingTimeLine/UpcomingWorkflowStageRequestDrawer';
import ConfirmUpcomingWorkflowStageRequest from 'src/components/VehicleTrackingDetails/VehicleTrackingTimeLine/ConfirmUpcomingWorkflowStageRequest';

export default function VehicleTrackingTimelineDesktop({ workFlowStageId }) {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const organisationId = authReducer.userData.organisations?.id;
  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawerStep, setDrawerStep] = useState('step1');
  const { vehicleTrackingNumber } = useParams();

  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const { vehicleTrackingDetails, timeline: timelineData } =
    vehicleTrackingDataReducer;

  useEffect(() => {
    dispatch(getTimelineData(vehicleTrackingNumber, organisationId));
  }, [dispatch, vehicleTrackingNumber, organisationId]);

  useEffect(() => {
    if (vehicleTrackingDetails?.hasPendingRequestedWorkflowStage) {
      dispatch(
        getUpcomingWorkflowStageRequest(vehicleTrackingNumber, organisationId),
      );
    }
  }, [
    vehicleTrackingDetails?.hasPendingRequestedWorkflowStage,
    dispatch,
    vehicleTrackingNumber,
    organisationId,
  ]);

  useEffect(
    () => () => {
      dispatch(resetTimelineData());
    },
    [dispatch],
  );

  const requestedStage = vehicleTrackingDetails?.requestedStage;

  const requestedStageExit = requestedStage?.exit_log_exist;
  const requestedStageEntry = requestedStage?.entry_log_exist;
  const canExitStage = !requestedStageExit && requestedStageEntry;
  const hasNextWorkflowStage = !(
    vehicleTrackingDetails?.nextWorkflowStageId === null &&
    vehicleTrackingDetails?.currentWorkflowStageCompleted === true
  );

  const handleCloseDrawer = () => {
    setDrawerStep('step1');
    setOpenDrawer(false);
  };

  // Find the last index of the current workflow stage
  const lastCurrentStageIndex = timelineData?.timeline?.findLastIndex(
    (item) => item?.workflow_stage_id === Number(workFlowStageId),
  );

  return (
    <div className="timeline-container">
      <div className="d-flex justify-content-between align-items-center timeline-header-v2">
        <div className="text-sm text-medium-bold">
          {vehicleTrackingDetails?.workflowName}
        </div>
        <div className="text-sm text-bold text-end">
          {hasNextWorkflowStage && (
            <div className="text-sm text-bold text-end">
              <ChangeStationIcon
                className="icon-size-lg cursor-pointer"
                onClick={() => setOpenDrawer(true)}
              />
            </div>
          )}
        </div>
      </div>
      <ul className="timeline-list-container theme-scrollable-container text-sm">
        {timelineData?.timeline?.map((logData, index) => {
          let className = 'timeline-progress text-sm';
          if (logData?.logs === null) {
            className = 'timeline-progress text-sm';
          } else if (
            logData?.logs?.entry_timestamp &&
            logData?.logs?.exit_timestamp &&
            !logData?.skipped
          ) {
            className = 'timeline-progress is-complete';
          } else if (logData?.workflow_stage_id === Number(workFlowStageId)) {
            className = 'timeline-progress is-current';
          } else if (
            (logData?.logs?.entry_timestamp || logData?.logs?.exit_timestamp) &&
            !logData?.skipped
          ) {
            className = 'timeline-progress is-pending';
          } else if (logData?.skipped) {
            className = 'timeline-progress is-skipped';
          }

          // Check if this is the last occurrence of the current workflow stage
          const isLastCurrentStage =
            logData?.workflow_stage_id === Number(workFlowStageId) &&
            index === lastCurrentStageIndex;

          return (
            <li className={className} key={logData?.workflow_stage_id}>
              <div className="time-text-header">
                <div className="div-flex-row cg-10 div-align-center">
                  <div className="div-flex-row time-text-header cg-10">
                    <div>
                      <span className="text-sm text-medium-bold">
                        {logData?.workflow_stage_name}
                      </span>
                    </div>
                    {isLastCurrentStage
                      ? !requestedStage?.completed &&
                        (canExitStage ? (
                          <div className="div-flex-row cg-5 div-align-center status-pill yellow">
                            <span className="text-xs">Awaiting Exit</span>
                          </div>
                        ) : (
                          <div className="div-flex-row cg-5 div-align-center status-pill blue">
                            <span className="text-xs">Awaiting Entry</span>
                          </div>
                        ))
                      : null}
                  </div>
                  {logData?.skipped && (
                    <div className="status-pill red">
                      <span className="text-xs">Skipped</span>
                    </div>
                  )}
                </div>
                <div className="log-line">
                  {logData?.logs?.entry_timestamp && (
                    <>
                      <EntryIcon className="icon-size-sm" color="#909090" />
                      <span className="text-sm text-grey left-padding">
                        {logData?.logs?.entry_timestamp}
                      </span>
                    </>
                  )}
                  <br />
                  {logData?.logs?.exit_timestamp && (
                    <>
                      <ExitIcon className="icon-size-sm" color="#909090" />
                      <span className="text-sm text-grey left-padding">
                        {logData?.logs?.exit_timestamp}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <Drawer
        anchor="right"
        open={Boolean(openDrawer)}
        onClose={() => {
          setDrawerStep('step1');
          setOpenDrawer(false);
        }}
        ModalProps={{ keepMounted: false }}
        sx={{ zIndex: 1 }}
        PaperProps={{
          sx: { width: '30%' },
        }}
      >
        {vehicleTrackingDetails?.hasPendingRequestedWorkflowStage ? (
          <ConfirmUpcomingWorkflowStageRequest
            vehicleTrackingDetails={vehicleTrackingDetails}
            workFlowStageId={workFlowStageId}
            onClose={handleCloseDrawer}
            setDrawerStep={setDrawerStep}
          />
        ) : drawerStep === 'step1' ? (
          <UpcomingWorkflowStageRequestDrawer
            vehicleTrackingDetails={vehicleTrackingDetails}
            workFlowStageId={workFlowStageId}
            onClose={handleCloseDrawer}
            setDrawerStep={setDrawerStep}
          />
        ) : (
          <ConfirmUpcomingWorkflowStageRequest
            vehicleTrackingDetails={vehicleTrackingDetails}
            workFlowStageId={workFlowStageId}
            onClose={handleCloseDrawer}
            setDrawerStep={setDrawerStep}
          />
        )}
      </Drawer>
    </div>
  );
}
